export type VideoConsultProvider = 'Jitsi' | 'Zoom' | 'GoogleMeet';

export function inferVideoProvider(
  url?: string | null,
  apiProvider?: string | null,
  corporateCompanyName?: string | null,
): VideoConsultProvider {
  const fromApi = (apiProvider ?? '').trim().toLowerCase();
  if (fromApi === 'jitsi') return 'Jitsi';
  if (fromApi === 'zoom') return 'Zoom';
  if (fromApi === 'googlemeet' || fromApi === 'google meet') return 'GoogleMeet';

  const href = (url ?? '').toLowerCase();
  if (href.includes('zoom.')) return 'Zoom';
  if (href.includes('meet.google')) return 'GoogleMeet';
  if (href.includes('/r/') || href.includes('rooms.') || href.includes('meet.aspen')) return 'Jitsi';

  return corporateCompanyName?.trim() ? 'Zoom' : 'GoogleMeet';
}

export function videoConsultProviderLabel(
  corporateCompanyName?: string | null,
  url?: string | null,
  apiProvider?: string | null,
): string {
  const provider = inferVideoProvider(url, apiProvider, corporateCompanyName);
  if (provider === 'Jitsi') return 'video consultation';
  if (provider === 'Zoom') return 'Zoom';
  return 'Google Meet';
}

/** Doctors join the room app as moderator. Meet/Zoom URLs are unchanged. */
export function withDoctorRoomRole(url: string, provider?: VideoConsultProvider | string | null): string {
  if (inferVideoProvider(url, provider) !== 'Jitsi')
    return url;

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('role', 'doctor');
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}role=doctor`;
  }
}

/** Staff join as moderator under their logged-in name. */
export function withStaffRoomRole(url: string, provider?: VideoConsultProvider | string | null): string {
  if (inferVideoProvider(url, provider) !== 'Jitsi')
    return url;

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('role', 'staff');
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}role=staff`;
  }
}

/** Put the portal JWT in the hash so the Meet tab can send it (not in query logs). */
export function withPortalAccessToken(url: string): string {
  const token = localStorage.getItem('accessToken');
  if (!token)
    return url;
  try {
    const parsed = new URL(url);
    parsed.hash = `access_token=${encodeURIComponent(token)}`;
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Staff/doctor portal iframe. Patients never use this. */
export function withRoomEmbed(url: string, provider?: VideoConsultProvider | string | null): string {
  if (inferVideoProvider(url, provider) !== 'Jitsi')
    return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('embed', '1');
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}embed=1`;
  }
}

export function consultTokenFromMeetUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const i = parts.indexOf('r');
    return i >= 0 && parts[i + 1] ? parts[i + 1] : null;
  } catch {
    return null;
  }
}

/** Strip hash/embed so we can persist a stable join URL and rebuild iframe src. */
export function normalizeMeetJoinUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.hash = '';
    parsed.searchParams.delete('embed');
    return parsed.toString();
  } catch {
    return url;
  }
}

export function meetOriginFromUrl(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export function openMeetInNewTab(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** @deprecated Prefer videoConsultProviderLabel with the visit URL. */
export function isCorporateVisit(corporateCompanyName?: string | null): boolean {
  return Boolean(corporateCompanyName?.trim());
}
